import { useMemo } from "react";
import { useRoles } from "../../../../../roles/use-roles";
import type { Option } from "../refs-context";

/** The permission that lets a member work (and so own) leads, and the scope of a role assignment that grants it everywhere. */
const LEAD_PERMISSION = "leads.edit";
const EVERYWHERE = "all";

interface Candidate {
  user_id: string;
  name: string;
  assignments: readonly { role_id: string; scope_type: string }[];
}

interface RoleGrant {
  id: string;
  permissions: readonly string[];
}

/** Whether a member holds `leads.edit` in every agent through one of their roles: the members a new lead can be given to. */
export function canWorkLeads(member: Pick<Candidate, "assignments">, roles: readonly RoleGrant[]): boolean {
  return member.assignments.some((assignment) => assignment.scope_type === EVERYWHERE && (roles.find((role) => role.id === assignment.role_id)?.permissions ?? []).includes(LEAD_PERMISSION));
}

/** The members who can own a lead, or `undefined` while their roles are not known (the picker then offers every member and the server decides). */
export function useLeadOwners(enabled: boolean, members: readonly Candidate[] | undefined): Option[] | undefined {
  const roles = useRoles(enabled);
  return useMemo(
    () => (roles.data && members ? members.filter((member) => canWorkLeads(member, roles.data)).map((member) => ({ id: member.user_id, name: member.name })) : undefined),
    [roles.data, members],
  );
}
